import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import AdminUsersReportPage from './AdminUsersReportPage';
import InternalDashboard from './InternalDashboard';
import InternalReportsPage from './InternalReportsPage';
import ScanRegulatoryReportContainer from './containers/Reports/ScanRegulatoryReportContainer';
import InternalUserAdministrationPage from './InternalUserAdministrationPage';
import OrganizationQuickstartMergePage from './OrganizationQuickstartMergePage';
import OrganizationMergePage from './OrganizationMergePage';
import PackageSummaryPage from './PackageSummaryPage';
import StagedPackagePromotionPage from './StagedPackagePromotionPage';
import CoaVisibilityPage from './CoaVisibilityPage';
import StagedPackageSummaryPage from './StagedPackageSummaryPage';
import FacilitySearchPage from './FacilitySearchPage';
import SuperUserManagementPage from './SuperUserManagementPage';
import OrganizationFacilitiesAndEmailsReportContainer from './containers/Organizations/OrganizationFacilitiesAndEmailsReportContainer';
import PrivateRoute from './PrivateRoute';
import UserManagementPage from './UserManagementPage';
import PreviewLayoutsAdminPage from './PreviewLayoutsAdminPage';
import LandingPagesAdminPage, { LandingPageEditorPage } from './LandingPagesAdminPage';
import { useLegacyAppSession } from './contexts/LegacyAppSessionContext';

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="stack" style={{ padding: '2rem' }} data-testid="internal-error">
      <h2>Something went wrong</h2>
      <pre style={{ color: 'var(--error, red)', whiteSpace: 'pre-wrap' }}>
        {message}
      </pre>
      <div>
        <button type="button" className="button cta" onClick={resetErrorBoundary}>
          Try again
        </button>
      </div>
    </div>
  );
}

 //parent layout for all /internal/* routes.
 //if a child route needs a *different* permission level, wrap its element with
 //<PrivateRoute requiredAction="admin:write"> inline.

export default function InternalPage({ organizationId }) {
  const location = useLocation();
  const { currentUser } = useLegacyAppSession();

  return (
    <ErrorBoundary
      resetKeys={[location.pathname]}
      FallbackComponent={Fallback}
    >
      <div className="stack">
        <Routes>
          <Route index element={<InternalDashboard currentUser={currentUser} />} />
          <Route path="landing-pages" element={<PrivateRoute requireSuperUser requiredAction="ManageInternalLandingPages"><LandingPagesAdminPage /></PrivateRoute>} />
          <Route path="landing-pages/:marketId/edit" element={<PrivateRoute requireSuperUser requiredAction="ManageInternalLandingPages"><LandingPageEditorPage /></PrivateRoute>} />
          <Route path="organizations/merge" element={<PrivateRoute requireSuperUser requiredAction="AdminInternalMergeQuickstartOrganizations"><OrganizationQuickstartMergePage organizationId={organizationId} /></PrivateRoute>} />
          <Route path="organizations/merge-organizations" element={<PrivateRoute requireSuperUser requiredAction="AdminInternalMergeQuickstartOrganizations"><OrganizationMergePage organizationId={organizationId} /></PrivateRoute>} />
          <Route path="users/super-users" element={<PrivateRoute requireSuperUser requiredAction="AdminInternalSuperuserManagement"><SuperUserManagementPage /></PrivateRoute>} />
          <Route path="package-summary" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalPackageSummary"><PackageSummaryPage /></PrivateRoute>} />
          <Route path="package-promotion" element={<PrivateRoute requireSuperUser requiredAction="WriteInternalStagedPackagePromotion"><StagedPackagePromotionPage /></PrivateRoute>} />
          <Route path="coa-visibility" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalCoaVisibility"><CoaVisibilityPage /></PrivateRoute>} />
          <Route path="staged-package-summary" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalStagedPackageSummary"><StagedPackageSummaryPage /></PrivateRoute>} />
          <Route path="facility-search" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalFacilitySearch"><FacilitySearchPage /></PrivateRoute>} />
          <Route path="users/administration" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalUserRoles"><InternalUserAdministrationPage organizationId={organizationId} currentUser={currentUser} /></PrivateRoute>} />
          <Route path="users/user-management" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalManualUserVerification"><UserManagementPage currentUser={currentUser} /></PrivateRoute>} />
          <Route path="preview-layouts" element={<PrivateRoute requireSuperUser requiredAction="ReadInternalPreviewLayouts"><PreviewLayoutsAdminPage currentUser={currentUser} /></PrivateRoute>} />
          <Route path="reports" element={<PrivateRoute requiredAction="ReadInternalReports"><InternalReportsPage currentUser={currentUser} /></PrivateRoute>} />
          <Route path="reports/facilities-and-emails-report" element={<PrivateRoute requiredAction="ReadInternalReports"><OrganizationFacilitiesAndEmailsReportContainer /></PrivateRoute>} />
          <Route path="reports/admin-users-report" element={<PrivateRoute requiredAction="ReadInternalReports"><AdminUsersReportPage currentUser={currentUser} /></PrivateRoute>} />
          <Route path="reports/scan-regulatory" element={<PrivateRoute requiredAction="ReadInternalReports"><ScanRegulatoryReportContainer currentUser={currentUser} /></PrivateRoute>} />
          <Route path="reports/retail-id" element={<Navigate to="/internal/reports/scan-regulatory" replace />} />
          <Route path="reports/organization-facilities-and-emails" element={<Navigate to="/internal/reports/facilities-and-emails-report" replace />} />
          {/* <Route path="super-users" element={<SuperUsers />} /> */}
          <Route path="*" element={<Navigate to="/internal" replace />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}
